import React, { useEffect, useState } from 'react';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  HashtagIcon,
  CheckCircleIcon,
  NoSymbolIcon,
  ReceiptPercentIcon,
  PlayIcon,
  PauseIcon,
} from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import { type CreateHSNCodeDto, type HSNCode, type HSNSortField } from '../../services/hsncode.service';
import {
  useHSNCodesPage,
  useHSNCodeSummary,
  useCreateHSNCode,
  useUpdateHSNCode,
  useDeleteHSNCode,
} from '../../hooks/useHSNCodes';
import HSNCodeForm from '../../components/hsn-codes/HSNCodeForm';
import { Modal } from '../../components/ui/Modal';
import { Pagination } from '../../components/ui/Pagination';
import {
  ConfirmDialog,
  DetailCard,
  DetailItem,
  FormActions,
  badgeClass,
  formatNumber,
  getApiErrorMessage,
  primaryButtonClass,
  secondaryButtonClass,
} from '../../components/ui/FormControls';
import { defaultRowsPerPage } from '../../lib/preferences';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const SORT_OPTIONS: { value: string; label: string; sortBy: HSNSortField; sortOrder: 'ASC' | 'DESC' }[] = [
  { value: 'code', label: 'Code, lowest first', sortBy: 'code', sortOrder: 'ASC' },
  { value: 'description', label: 'Description A–Z', sortBy: 'description', sortOrder: 'ASC' },
  { value: 'gstRate', label: 'Highest GST rate', sortBy: 'gstRate', sortOrder: 'DESC' },
  { value: 'createdAt', label: 'Newest first', sortBy: 'createdAt', sortOrder: 'DESC' },
];

const formatRate = (rate: number) => `${formatNumber(rate, 2)}%`;

const formatDate = (value?: string | null) =>
  value
    ? new Date(value).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
    : '—';

const HSNCodeList: React.FC = () => {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'active' | 'inactive' | ''>('');
  const [rateFilter, setRateFilter] = useState('');
  const [sort, setSort] = useState(SORT_OPTIONS[0].value);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(defaultRowsPerPage);

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingCode, setEditingCode] = useState<HSNCode | null>(null);
  const [viewCode, setViewCode] = useState<HSNCode | null>(null);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<HSNCode | null>(null);
  const [toggleTarget, setToggleTarget] = useState<HSNCode | null>(null);

  // Debounce search so the API is not called on every keystroke
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const sortOption = SORT_OPTIONS.find((option) => option.value === sort) || SORT_OPTIONS[0];
  const { data: codePage, isLoading, isFetching, error } = useHSNCodesPage({
    page,
    limit: pageSize,
    search: debouncedSearch || undefined,
    isActive: statusFilter ? statusFilter === 'active' : undefined,
    gstRate: rateFilter === '' ? undefined : Number(rateFilter),
    sortBy: sortOption.sortBy,
    sortOrder: sortOption.sortOrder,
  });
  const { data: summary, isLoading: isSummaryLoading } = useHSNCodeSummary();
  const createMutation = useCreateHSNCode();
  const updateMutation = useUpdateHSNCode();
  const deleteMutation = useDeleteHSNCode();

  const hsnCodes = codePage?.data ?? [];
  const total = codePage?.total ?? 0;
  const totalPages = codePage?.totalPages ?? 1;
  const isSaving = createMutation.isPending || (updateMutation.isPending && isFormOpen);

  // Step back when the current page no longer exists
  useEffect(() => {
    if (codePage && page > 1 && page > codePage.totalPages) {
      setPage(Math.max(1, codePage.totalPages));
    }
  }, [codePage, page]);

  const openCreate = () => {
    setEditingCode(null);
    setIsFormOpen(true);
  };

  const openEdit = (hsnCode: HSNCode) => {
    setEditingCode(hsnCode);
    setIsFormOpen(true);
  };

  const openView = (hsnCode: HSNCode) => {
    setViewCode(hsnCode);
    setIsViewOpen(true);
  };

  const handleSubmit = async (data: CreateHSNCodeDto) => {
    try {
      if (editingCode) {
        await updateMutation.mutateAsync({ id: editingCode.id, data });
        toast.success('HSN code updated successfully');
      } else {
        await createMutation.mutateAsync(data);
        toast.success('HSN code created successfully');
        setPage(1);
      }
      setIsFormOpen(false);
    } catch (err) {
      toast.error(getApiErrorMessage(err, editingCode ? 'Could not update the HSN code' : 'Could not create the HSN code'));
      throw err;
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteMutation.mutateAsync(deleteTarget.id);
      toast.success('HSN code deleted successfully');
      setDeleteTarget(null);
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not delete the HSN code'));
    }
  };

  const confirmToggle = async () => {
    if (!toggleTarget) return;
    try {
      await updateMutation.mutateAsync({ id: toggleTarget.id, data: { isActive: !toggleTarget.isActive } });
      toast.success(toggleTarget.isActive ? 'HSN code marked inactive' : 'HSN code reactivated');
      setToggleTarget(null);
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not change the HSN code status'));
    }
  };

  const hasFilters = !!(search || statusFilter || rateFilter !== '');

  const clearFilters = () => {
    setSearch('');
    setStatusFilter('');
    setRateFilter('');
    setPage(1);
  };

  const rates = summary?.rates ?? [];
  const stats = [
    {
      label: 'Total HSN Codes',
      value: formatNumber(summary?.total ?? 0),
      hint: 'Codes on file',
      icon: HashtagIcon,
      tone: 'text-indigo-600 bg-indigo-50',
    },
    {
      label: 'Active',
      value: formatNumber(summary?.active ?? 0),
      hint: 'Offered on new invoices',
      icon: CheckCircleIcon,
      tone: 'text-green-600 bg-green-50',
    },
    {
      label: 'Inactive',
      value: formatNumber(summary?.inactive ?? 0),
      hint: 'Kept for old invoices only',
      icon: NoSymbolIcon,
      tone: 'text-gray-500 bg-gray-100',
    },
    {
      label: 'GST Rates in Use',
      value: formatNumber(rates.length),
      hint: rates.length > 0 ? rates.map((rate) => formatRate(rate.gstRate)).join(', ') : 'No codes yet',
      icon: ReceiptPercentIcon,
      tone: 'text-teal-600 bg-teal-50',
    },
  ];

  const selectClass =
    'h-10 rounded-md border border-gray-300 bg-white px-3 text-sm text-gray-700 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500';
  const thClass = 'px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500';
  const iconButtonClass = 'p-1.5 rounded disabled:opacity-50';

  const renderStatus = (hsnCode: HSNCode) => (
    <span
      className={`${badgeClass} ${
        hsnCode.isActive ? 'text-green-700 bg-green-50 ring-green-600/20' : 'text-gray-700 bg-gray-50 ring-gray-500/20'
      }`}
    >
      {hsnCode.isActive ? 'Active' : 'Inactive'}
    </span>
  );

  const usageText = (hsnCode: HSNCode) => {
    const lines = hsnCode.invoiceLines ?? 0;
    return lines === 0 ? 'Not used yet' : `${formatNumber(lines)} invoice ${lines === 1 ? 'line' : 'lines'}`;
  };

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">Error loading HSN codes</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">HSN Codes</h1>
          <p className="mt-1 text-sm text-gray-600">The codes and GST rates that invoice lines are taxed by</p>
        </div>
        <button
          type="button"
          onClick={openCreate}
          className="inline-flex items-center justify-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          Add HSN Code
        </button>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {stats.map((stat) => (
          <div
            key={stat.label}
            className="flex items-center gap-4 rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-200"
          >
            <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-lg ${stat.tone}`}>
              <stat.icon className="h-6 w-6" />
            </div>
            <div className="min-w-0">
              <p className="text-sm text-gray-500">{stat.label}</p>
              <p className="truncate text-xl font-semibold text-gray-900">{isSummaryLoading ? '—' : stat.value}</p>
              <p className="truncate text-xs text-gray-500">{isSummaryLoading ? ' ' : stat.hint}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-gray-200">
        {/* Search & filters */}
        <div className="flex flex-col gap-3 border-b border-gray-200 p-4 lg:flex-row lg:items-center">
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by code or description..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="block h-10 w-full rounded-md border border-gray-300 bg-white pl-10 pr-3 text-sm text-gray-900 shadow-sm placeholder:text-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <select
              aria-label="Filter by status"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value as 'active' | 'inactive' | '');
                setPage(1);
              }}
              className={selectClass}
            >
              <option value="">All statuses</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
            <select
              aria-label="Filter by GST rate"
              value={rateFilter}
              onChange={(e) => {
                setRateFilter(e.target.value);
                setPage(1);
              }}
              className={selectClass}
            >
              <option value="">All GST rates</option>
              {rates.map((rate) => (
                <option key={rate.gstRate} value={String(rate.gstRate)}>
                  {formatRate(rate.gstRate)} ({rate.codes})
                </option>
              ))}
            </select>
            <select
              aria-label="Sort HSN codes"
              value={sort}
              onChange={(e) => {
                setSort(e.target.value);
                setPage(1);
              }}
              className={selectClass}
            >
              {SORT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {hasFilters && (
              <button
                type="button"
                onClick={clearFilters}
                className="inline-flex h-10 items-center rounded-md px-2 text-sm font-medium text-gray-600 hover:text-gray-900"
              >
                <XMarkIcon className="h-4 w-4 mr-1" />
                Clear
              </button>
            )}
          </div>
        </div>

        {/* HSN codes table */}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className={thClass}>HSN Code</th>
                <th className={thClass}>Description</th>
                <th className={thClass}>GST Rate</th>
                <th className={`${thClass} text-right`}>Cess</th>
                <th className={thClass}>Unit</th>
                <th className={thClass}>Used On</th>
                <th className={thClass}>Status</th>
                <th className={`${thClass} text-right`}>Actions</th>
              </tr>
            </thead>
            <tbody className={`divide-y divide-gray-100 bg-white ${isFetching && !isLoading ? 'opacity-60' : ''}`}>
              {isLoading ? (
                <tr>
                  <td className="px-4 py-12 text-center text-sm text-gray-500" colSpan={8}>
                    Loading HSN codes...
                  </td>
                </tr>
              ) : hsnCodes.length === 0 ? (
                <tr>
                  <td className="px-4 py-12 text-center" colSpan={8}>
                    <HashtagIcon className="mx-auto h-10 w-10 text-gray-300" />
                    <p className="mt-2 text-sm font-medium text-gray-900">No HSN codes found</p>
                    <p className="mt-1 text-sm text-gray-500">
                      {hasFilters
                        ? 'Try changing or clearing the search and filters.'
                        : 'Add the HSN codes of what you sell so invoices can pick up their GST rates.'}
                    </p>
                  </td>
                </tr>
              ) : (
                hsnCodes.map((hsnCode) => (
                  <tr key={hsnCode.id} className="hover:bg-gray-50">
                    <td className="whitespace-nowrap px-4 py-3 text-sm">
                      <span className="rounded bg-gray-100 px-2 py-0.5 font-mono text-xs text-gray-700">{hsnCode.code}</span>
                      <div className="mt-1 text-xs text-gray-500">Chapter {hsnCode.chapter || hsnCode.code.slice(0, 2)}</div>
                    </td>
                    <td className="max-w-md px-4 py-3 text-sm">
                      <div className="text-gray-900">{hsnCode.description}</div>
                      {hsnCode.notes && <div className="mt-0.5 truncate text-xs text-gray-500">{hsnCode.notes}</div>}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm">
                      <span className={`${badgeClass} text-indigo-700 bg-indigo-50 ring-indigo-600/20 tabular-nums`}>
                        {formatRate(hsnCode.gstRate)}
                      </span>
                      <div className="mt-1 text-xs text-gray-500 tabular-nums">
                        CGST {formatRate(hsnCode.cgstRate)} + SGST {formatRate(hsnCode.sgstRate)}
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-gray-700 tabular-nums">
                      {hsnCode.cessRate > 0 ? formatRate(hsnCode.cessRate) : '—'}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">{hsnCode.unit || '—'}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">{usageText(hsnCode)}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm">{renderStatus(hsnCode)}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-1">
                        <button
                          onClick={() => setToggleTarget(hsnCode)}
                          className={`inline-flex items-center rounded-md px-2.5 py-1 text-xs font-semibold ${
                            hsnCode.isActive
                              ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                              : 'bg-green-50 text-green-700 hover:bg-green-100'
                          }`}
                        >
                          {hsnCode.isActive ? (
                            <PauseIcon className="h-3.5 w-3.5 mr-1" />
                          ) : (
                            <PlayIcon className="h-3.5 w-3.5 mr-1" />
                          )}
                          {hsnCode.isActive ? 'Deactivate' : 'Activate'}
                        </button>
                        <button
                          onClick={() => openView(hsnCode)}
                          className={`${iconButtonClass} text-gray-600 hover:bg-gray-100 hover:text-gray-900`}
                          title="View Details"
                          aria-label="View Details"
                        >
                          <EyeIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => openEdit(hsnCode)}
                          className={`${iconButtonClass} text-indigo-600 hover:bg-indigo-50 hover:text-indigo-900`}
                          title="Edit HSN Code"
                          aria-label="Edit HSN Code"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => setDeleteTarget(hsnCode)}
                          className={`${iconButtonClass} text-red-600 hover:bg-red-50 hover:text-red-900`}
                          title="Delete HSN Code"
                          aria-label="Delete HSN Code"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {!isLoading && total > 0 && (
          <div className="flex flex-col gap-3 border-t border-gray-200 px-4 py-3 lg:flex-row lg:items-center lg:gap-6">
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-gray-500">
              <span>
                Showing {formatNumber((page - 1) * pageSize + 1)}–
                {formatNumber(Math.min(page * pageSize, total))} of {formatNumber(total)} HSN codes
              </span>
              <label className="flex items-center gap-2">
                Rows per page
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setPage(1);
                  }}
                  className="h-8 rounded-md border border-gray-300 bg-white px-2 text-sm text-gray-700 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                >
                  {PAGE_SIZE_OPTIONS.map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <Pagination className="lg:flex-1" currentPage={page} totalPages={totalPages} onPageChange={setPage} />
          </div>
        )}
      </div>

      {/* Create or edit */}
      <Modal
        isOpen={isFormOpen}
        onClose={() => !isSaving && setIsFormOpen(false)}
        title={editingCode ? 'Edit HSN Code' : 'Add HSN Code'}
        description={
          editingCode
            ? `${editingCode.code} · ${editingCode.description}`
            : 'Invoice lines with this code pick up its GST rate.'
        }
        size="xl"
        className="overflow-visible"
      >
        <HSNCodeForm
          key={editingCode ? `${editingCode.id}-${editingCode.updatedAt}` : 'new'}
          hsnCode={editingCode}
          onSubmit={handleSubmit}
          onCancel={() => setIsFormOpen(false)}
          isSaving={isSaving}
        />
      </Modal>

      {/* Details */}
      <Modal isOpen={isViewOpen} onClose={() => setIsViewOpen(false)} title="HSN Code Details" size="lg">
        {viewCode && (
          <div className="space-y-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <p>
                  <span className="rounded bg-gray-100 px-2 py-0.5 font-mono text-sm text-gray-700">{viewCode.code}</span>
                </p>
                <h4 className="mt-2 text-base font-semibold text-gray-900">{viewCode.description}</h4>
              </div>
              {renderStatus(viewCode)}
            </div>

            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
              {[
                { label: 'GST', value: formatRate(viewCode.gstRate), tone: 'bg-indigo-50 text-indigo-900' },
                { label: 'CGST', value: formatRate(viewCode.cgstRate), tone: 'bg-gray-50 text-gray-900' },
                { label: 'SGST', value: formatRate(viewCode.sgstRate), tone: 'bg-gray-50 text-gray-900' },
                { label: 'IGST', value: formatRate(viewCode.igstRate), tone: 'bg-gray-50 text-gray-900' },
              ].map((item) => (
                <div key={item.label} className={`rounded-lg px-4 py-3 ${item.tone}`}>
                  <p className="text-xs font-medium uppercase tracking-wide opacity-70">{item.label}</p>
                  <p className="mt-1 text-lg font-semibold tabular-nums">{item.value}</p>
                </div>
              ))}
            </div>

            <DetailCard title="Classification and use" columns={3}>
              <DetailItem label="Chapter">{viewCode.chapter || viewCode.code.slice(0, 2)}</DetailItem>
              <DetailItem label="Heading">{viewCode.heading || viewCode.code.slice(0, 4)}</DetailItem>
              <DetailItem label="Unit">{viewCode.unit || '—'}</DetailItem>
              <DetailItem label="Cess">{viewCode.cessRate > 0 ? formatRate(viewCode.cessRate) : 'None'}</DetailItem>
              <DetailItem label="Used on">{usageText(viewCode)}</DetailItem>
              <DetailItem label="Last updated">{formatDate(viewCode.updatedAt)}</DetailItem>
              {viewCode.notes && (
                <DetailItem label="Notes" className="col-span-2 lg:col-span-3">
                  <span className="whitespace-pre-line font-normal text-gray-700">{viewCode.notes}</span>
                </DetailItem>
              )}
            </DetailCard>
          </div>
        )}

        <FormActions>
          <button type="button" onClick={() => setIsViewOpen(false)} className={secondaryButtonClass}>
            Close
          </button>
          {viewCode && (
            <button
              type="button"
              onClick={() => {
                setIsViewOpen(false);
                openEdit(viewCode);
              }}
              className={primaryButtonClass}
            >
              <PencilIcon className="mr-2 h-4 w-4" />
              Edit HSN Code
            </button>
          )}
        </FormActions>
      </Modal>

      {/* Activate / deactivate confirmation */}
      <ConfirmDialog
        isOpen={!!toggleTarget}
        onClose={() => setToggleTarget(null)}
        onConfirm={confirmToggle}
        title={toggleTarget?.isActive ? 'Deactivate HSN Code' : 'Activate HSN Code'}
        message={
          <>
            <span className="font-mono font-medium text-gray-900">{toggleTarget?.code}</span>{' '}
            {toggleTarget?.isActive
              ? 'will no longer be offered on new invoices. Invoices that already use it are not changed.'
              : 'will be offered on new invoices again.'}
          </>
        }
        confirmLabel={toggleTarget?.isActive ? 'Deactivate' : 'Activate'}
        pendingLabel="Updating..."
        isPending={updateMutation.isPending}
        tone="primary"
      />

      {/* Delete confirmation */}
      <ConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        title="Delete HSN Code"
        message={
          <>
            <span className="font-mono font-medium text-gray-900">{deleteTarget?.code}</span> ({deleteTarget?.description})
            will be permanently deleted. This cannot be undone. A code that invoices already use cannot be deleted and
            should be deactivated instead.
          </>
        }
        confirmLabel="Delete"
        pendingLabel="Deleting..."
        isPending={deleteMutation.isPending}
      />
    </div>
  );
};

export default HSNCodeList;
